import {ChangeDetectionStrategy, Component, OnInit, inject} from '@angular/core';
import {NgxIndexedDBService} from 'ngx-indexed-db';
import {IndexedDBConstants} from './shared/constants/indexedDBConstants';
import {firstValueFrom, Observable} from 'rxjs';
import {LoadingService} from './core/services/loading.service';
import {TelemetryService} from './shared/services/telemetry.service';
import {RouterOutlet} from '@angular/router';
import {AsyncPipe} from '@angular/common';

/**
 * Main app component (application root).
 *
 * OnPush since Phase 12 C-6-vi-b. The deferral rationale (FileDisplayComponent
 * writing plain template-read fields from `.subscribe()` callbacks under a
 * non-dirty-marking ancestor) is resolved by C-6: FileDisplay is OnPush at
 * `b5411117` and routes its template state through `FileDisplayStore` +
 * `FileAnalysisStore` signals, each of which marks its own view dirty.
 * `isLoading$ | async` continues to handle this host's own re-render.
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, AsyncPipe],
})
export class AppComponent implements OnInit {
    private indexedDbService = inject(NgxIndexedDBService);
    private loadingService = inject(LoadingService);
    private telemetry = inject(TelemetryService);

    title = 'PIDB';
    isLoading$: Observable<boolean>;

    constructor() {
        this.isLoading$ = this.loadingService.isLoading$;
    }

    async ngOnInit() {
        // Wire LCP/INP/CLS observers once per session. Safe to call before the
        // first navigation completes — `web-vitals` defers measurement until
        // the underlying PerformanceObserver entries fire.
        this.telemetry.registerWebVitals();
        firstValueFrom(this.indexedDbService.getAll(IndexedDBConstants.oldDocument)).catch(() => {
            this.indexedDbService.createObjectStore({
                store: IndexedDBConstants.oldDocument,
                storeConfig: {keyPath: 'name', autoIncrement: false},
                storeSchema: [],
            });
        });
    }
}
